/**
 * Global Product Quick View (Section Rendering API)
 *
 * Any element with [data-global-quick-view] opens the shared quick view:
 *   <button data-global-quick-view data-product-url="/products/handle" data-variant-id="123">Quick view</button>
 * An <a data-global-quick-view href="/products/handle"> also works (href is used as the URL).
 *
 * Performance:
 *  - One delegated listener per event type, no per-card JS, no per-card dialog markup.
 *  - Markup is fetched only on intent (hover / focus / touch) and on click.
 *  - Responses are memoised (small LRU) and Shopify edge-caches section responses.
 *  - Prefetch is skipped on Save-Data / 2G connections.
 *
 * The dialog uses the Shop The Look quick view markup and stylesheet. Swatches
 * inside the dialog update through that same behaviour. When the shopper
 * changes the main PDP variant, triggers are rewritten from the freshly
 * rendered section and an open dialog is fetched again for the new variant.
 *
 * Events (on document):
 *  - global-quick-view:loaded  detail: { dialog, trigger, productUrl }   → re-init hooks
 *  - global-quick-view:closed  detail: { dialog }
 */

import './quick-view-multi-option.js';
import { refreshQuickViewLayout } from './shop-the-look-quick-view.js';
import {
  TRIGGER_SELECTOR,
  allowPrefetch,
  buildProductUrl,
  buildSectionUrl,
  clearQuickViewAdding,
  markQuickViewAdding,
  shouldMarkQuickViewAdding,
  syncQuickViewTriggers as syncQuickViewHosts,
  triggerFromSlot as triggerFromSlotIn,
  triggerSlot as triggerSlotIn,
} from './global-quick-view-logic.js';

const SECTION_ID = 'global-quick-view';
const DIALOG_ID = 'global-quick-view';
const LOADING_TEMPLATE_ID = 'global-quick-view-loading';
const CACHE_LIMIT = 20;
const PREFETCH_DELAY = 80; // ms of hover before we prefetch
const CLOSE_ON_ADD_TO_CART = true;

const cache = new Map(); // url -> Promise<string>
let requestToken = 0;
let lastTrigger = null;
let hoverTimer = null;

const getDialog = () => document.getElementById(DIALOG_ID);

/* ----------------------------- URL + fetching ---------------------------- */

function sectionUrl(trigger) {
  return buildSectionUrl(
    trigger.dataset.productUrl || trigger.getAttribute('href'),
    trigger.dataset.variantId,
    window.location.origin,
    SECTION_ID
  );
}

function productUrl(trigger) {
  return buildProductUrl(
    trigger.dataset.productUrl || trigger.getAttribute('href'),
    trigger.dataset.variantId,
    window.location.origin
  );
}

function loadSection(url) {
  if (cache.has(url)) {
    // refresh LRU position
    const hit = cache.get(url);
    cache.delete(url);
    cache.set(url, hit);
    return hit;
  }

  const request = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
    .then((response) => {
      if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      cache.delete(url); // don't cache failures
      throw error;
    });

  cache.set(url, request);
  if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  return request;
}

function canPrefetch() {
  return allowPrefetch(navigator.connection);
}

/* ------------------------------- Dialog ---------------------------------- */

function showLoading(dialog) {
  const template = document.getElementById(LOADING_TEMPLATE_ID);
  dialog.setAttribute('aria-busy', 'true');
  dialog.replaceChildren(template ? template.content.cloneNode(true) : '');
}

function injectContent(dialog, html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const source = doc.getElementById(DIALOG_ID);
  if (!source) throw new Error('Quick view markup not found in section response');

  // Keep the shell element (and its listeners); swap only its children.
  // Custom elements (slideshow, product-form-component, add-to-cart-component)
  // upgrade automatically when inserted into the live document.
  dialog.replaceChildren(...source.childNodes);
  dialog.removeAttribute('aria-busy');
}

/**
 * @param {HTMLElement} trigger
 * @param {{ refocus?: boolean }} [options]
 */
async function open(trigger, options = {}) {
  const dialog = getDialog();
  const url = sectionUrl(trigger);
  if (!dialog || !url) return false;

  const token = ++requestToken;
  lastTrigger = trigger;
  const refocus = options.refocus !== false;
  trigger.setAttribute('aria-busy', 'true');
  dialog.setAttribute('aria-busy', 'true');

  if (!dialog.open) {
    showLoading(dialog);
    dialog.showModal();
    document.documentElement.classList.add('quick-view-open');
  }

  try {
    const html = await loadSection(url);
    if (token !== requestToken || !dialog.open) return true; // stale or closed meanwhile

    injectContent(dialog, html);
    refreshQuickViewLayout(dialog);
    if (refocus) dialog.querySelector('[data-stl-qv-close]')?.focus({ preventScroll: true });

    document.dispatchEvent(
      new CustomEvent('global-quick-view:loaded', {
        detail: { dialog, trigger, productUrl: productUrl(trigger) },
      })
    );
  } catch (error) {
    console.error(error);
    if (token === requestToken) window.location.href = productUrl(trigger); // graceful fallback
  } finally {
    trigger.removeAttribute('aria-busy');
  }
  return true;
}

function close() {
  const dialog = getDialog();
  if (dialog?.open) dialog.close();
}

function onDialogClose() {
  const dialog = getDialog();
  requestToken++; // cancel any in-flight render
  document.documentElement.classList.remove('quick-view-open');
  dialog.replaceChildren(); // free DOM + reset form state
  document.dispatchEvent(new CustomEvent('global-quick-view:closed', { detail: { dialog } }));

  if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
  lastTrigger = null;
}

/* ------------------------- Main PDP variant change ----------------------- */

function triggerSlot(trigger) {
  return triggerSlotIn(trigger, document);
}

function triggerFromSlot(slot) {
  return triggerFromSlotIn(slot, document);
}

function syncQuickViewTriggers(doc) {
  return syncQuickViewHosts(document, doc);
}

function handleMainVariantChange(doc) {
  const dialog = getDialog();
  const wasOpen = Boolean(dialog?.open);
  const slot = wasOpen ? triggerSlot(lastTrigger) : null;
  const previousUrl = wasOpen && lastTrigger ? sectionUrl(lastTrigger) : null;

  const changed = syncQuickViewTriggers(doc);
  if (changed) cache.clear();
  if (!wasOpen || !dialog) return;

  const trigger = triggerFromSlot(slot) || (lastTrigger?.isConnected ? lastTrigger : null);
  if (!(trigger instanceof HTMLElement) || trigger.closest('[hidden]')) {
    if (wasOpen) close();
    return;
  }
  if (sectionUrl(trigger) === previousUrl) return;
  open(trigger, { refocus: false });
}

/* ------------------------------- Listeners ------------------------------- */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest(TRIGGER_SELECTOR);
  if (!trigger) return;

  // Let modified clicks (new tab, etc.) behave like a normal link.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

  event.preventDefault();
  event.stopPropagation(); // stop product-card / DY click handlers from also firing
  open(trigger);
});

function prefetchFrom(event) {
  const trigger = event.target.closest?.(TRIGGER_SELECTOR);
  if (!trigger || !canPrefetch()) return;
  const url = sectionUrl(trigger);
  if (url) loadSection(url).catch(() => {});
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;
  const trigger = event.target.closest(TRIGGER_SELECTOR);
  if (!trigger) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchFrom(event), PREFETCH_DELAY);
});

document.addEventListener('pointerout', (event) => {
  if (event.target.closest(TRIGGER_SELECTOR)) clearTimeout(hoverTimer);
});

document.addEventListener('focusin', prefetchFrom);
document.addEventListener('touchstart', prefetchFrom, { passive: true });

function bindDialog() {
  const dialog = getDialog();
  if (!dialog || dialog.dataset.globalQvBound) return;
  dialog.dataset.globalQvBound = 'true';

  // Runs after product-form-component's submit handler. Disabling earlier
  // makes that handler treat the click as a no-op.
  dialog.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement) || !dialog.contains(event.target)) return;
    const button = event.target.querySelector('[data-stl-qv-add-to-cart]');
    if (!shouldMarkQuickViewAdding(button)) return;
    markQuickViewAdding(button);
  });

  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-stl-qv-close]')) return close();

    // Backdrop click: target is the <dialog> itself AND the point is outside its box
    // (so clicks on the dialog's own padding don't close it).
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right &&
      event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) close();
  });
  dialog.addEventListener('close', onDialogClose);
}

if (CLOSE_ON_ADD_TO_CART) {
  // Horizon dispatches `cart:update` after a successful add; close so the cart drawer is visible.
  document.addEventListener('cart:update', (event) => {
    const dialog = getDialog();
    if (dialog?.open && (!event.target || event.target === document || dialog.contains(event.target))) close();
  });
}

// A failed add leaves the dialog open, so put the label back. A successful
// add closes the dialog on cart:update before cart:synced, so this is a no-op then.
document.addEventListener('cart:error', (event) => {
  const dialog = getDialog();
  if (!dialog?.open) return;
  if (event.target instanceof Node && !dialog.contains(event.target)) return;
  restoreQuickViewAddToCart();
});
document.addEventListener('cart:synced', restoreQuickViewAddToCart);

function restoreQuickViewAddToCart() {
  const dialog = getDialog();
  if (!dialog?.open) return;
  const button = dialog.querySelector('[data-stl-qv-add-to-cart][data-qv-adding="true"]');
  if (button instanceof HTMLButtonElement) clearQuickViewAdding(button);
}

bindDialog();

// Main PDP variant picker. The fetched page already contains Buy the Bundle /
// Purchase in Bulk / Complete Your Look rendered for the new variant. Copy
// those triggers (or the bulk product list) so the next open — and an open
// dialog — fetches that variant instead of the one from first paint.
document.addEventListener('variant:update', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.dataset.templateProductMatch !== 'true') return;
  const html = event.detail?.data?.html;
  if (!(html instanceof Document)) return;
  handleMainVariantChange(html);
});

// Public API for other scripts (e.g. DY recommendations rendered after load)
window.GlobalQuickView = {
  open,
  close,
  prefetch: (trigger) => {
    const url = sectionUrl(trigger);
    return url ? loadSection(url) : Promise.resolve('');
  },
};
